// Resolves a stored S3 image reference (either a bare/legacy S3 URL or a
// plain S3 key) to a real, working, presigned display URL via Amplify
// Storage's getUrl().
//
// BACKGROUND — why this exists (found 7 Jul 2026, reported as "event image
// doesn't display"):
//
// Event.imageUrl, Announcement.imageUrl, and Sponsor.logoUrl were all being
// populated at upload time with a bare S3 URL, e.g.
//   https://amplify-...-storage-....s3.ap-southeast-2.amazonaws.com/events/167...-photo.png
// by stripping the query string off a one-time presigned getUrl() result
// (`urlResult.url.toString().split('?')[0]`). That produces a URL with no
// signature at all.
//
// These paths (events/*, announcements/*, logos/sponsors/*) grant
// `allow.guest.to(['read'])` in amplify/storage/resource.ts, which is
// Amplify's Cognito-Identity-Pool-based *guest IAM role* access — it is
// NOT an S3 bucket policy making the object plain-HTTP-public. A bare
// <img src="https://bucket.s3.amazonaws.com/...">  request from a browser
// carries no AWS credentials at all, so it always 403s, regardless of the
// guest read grant. Confirmed directly: the one live production event
// image returns HTTP 403.
//
// The fix: never store a baked URL. Store the S3 key, and resolve it to a
// real URL *at render time* via getUrl() (which uses whatever Amplify Auth
// session is active — including the automatic guest session on public
// pages — to produce a correctly-signed request). This function also
// transparently upgrades already-broken legacy bare-URL values by
// extracting the key from them, so existing records self-heal on next
// render with no separate data migration required.

import { getUrl } from 'aws-amplify/storage';

/**
 * Extracts the S3 object key from a stored image reference.
 *
 * Handles three shapes:
 *  - A plain S3 key already (e.g. "events/167...-photo.png") — returned as-is.
 *  - A legacy bare S3 URL (e.g. "https://bucket.s3.region.amazonaws.com/events/...")
 *    — the key is everything after the bucket host's first "/".
 *  - Empty/undefined — returns null.
 */
export function extractStorageKey(stored: string | null | undefined): string | null {
  if (!stored) return null;
  if (!stored.startsWith('http://') && !stored.startsWith('https://')) {
    // Already a plain key.
    return stored;
  }
  try {
    const url = new URL(stored);
    // pathname starts with a leading "/" — strip it.
    const key = decodeURIComponent(url.pathname.replace(/^\/+/, ''));
    return key || null;
  } catch {
    return null;
  }
}

// Small in-memory cache so re-rendering the same image many times on one
// page (e.g. a list of announcements) doesn't fire a redundant getUrl()
// call per render. Presigned URLs from getUrl() are valid for 15 minutes;
// this cache is intentionally not persisted anywhere and is cleared on
// full page reload, which is the correct lifetime for a value that expires.
const urlCache = new Map<string, Promise<string | null>>();

/**
 * Resolves a stored image reference (key or legacy bare URL) to a real,
 * working display URL. Returns null if there is nothing to resolve, or if
 * resolution fails (e.g. the object no longer exists) — callers should
 * treat null the same as "no image".
 */
export async function resolveStorageImageUrl(stored: string | null | undefined): Promise<string | null> {
  const key = extractStorageKey(stored);
  if (!key) return null;

  const cached = urlCache.get(key);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const result = await getUrl({ path: key });
      return result.url.toString();
    } catch (err) {
      console.error(`Failed to resolve storage URL for key "${key}":`, err);
      return null;
    }
  })();

  urlCache.set(key, promise);
  return promise;
}
